import { LitElement, html, css } from 'lit';
import { store } from '../lib/store.js';
import { router } from '../lib/router.js';
import { browserUrl, withBuildPrefix } from '../lib/build-paths.js';
import './site-search-button.js';

export class SiteHeader extends LitElement {
  static properties = {
    _menuOpen: { state: true },
    _scrolled: { state: true },
    _pages: { state: true },
    _currentSlug: { state: true },
    _settings: { state: true },
  };

  static styles = css`
    :host {
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 100;
      transition: background var(--transition-base, 0.25s ease);
      color: var(--color-text-primary);
    }
    :host(.scrolled) {
      background: var(--color-bg-glass);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border-bottom: 1px solid var(--color-border);
      box-shadow: 0 10px 30px color-mix(in srgb, var(--color-text-primary) 8%, transparent);
    }
    .header-inner {
      max-width: var(--content-max-width, 1440px);
      margin: 0 auto;
      padding: 0 var(--content-padding, 2rem);
      height: var(--header-height, 4rem);
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .logo {
      display: flex;
      align-items: center;
      gap: var(--space-3, 0.75rem);
      min-height: 2.75rem;
      text-decoration: none;
      cursor: pointer;
    }
    .logo-icon {
      width: auto;
      min-width: 3.25rem;
      height: 2.35rem;
      padding: 0 0.45rem;
      box-sizing: border-box;
      background: var(--color-accent);
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      font-size: 1rem;
      font-style: italic;
      color: white;
      letter-spacing: 0;
      white-space: nowrap;
      box-shadow: 0 10px 22px color-mix(in srgb, var(--color-accent) 20%, transparent);
    }
    .logo-text {
      font-weight: var(--font-medium, 500);
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-primary, #f0f0f5);
      letter-spacing: 0;
    }
    .logo-text { color: var(--color-text-primary); }
    .logo-image {
      display: block;
      height: 2.5rem;
      max-width: 12rem;
      object-fit: contain;
    }
    .theme-toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.75rem;
      height: 2.75rem;
      border-radius: 50%;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      color: var(--color-text-primary);
      cursor: pointer;
      transition: all var(--transition-fast, 0.15s ease);
    }
    .theme-toggle:hover {
      color: var(--color-accent);
      border-color: var(--color-border-hover);
      transform: translateY(-1px);
    }
    nav {
      display: flex;
      align-items: center;
      gap: var(--space-1, 0.25rem);
    }
    .header-actions {
      display: flex;
      align-items: center;
      gap: var(--space-2, 0.5rem);
      margin-left: var(--space-2, 0.5rem);
    }
    .nav-link {
      padding: var(--space-2, 0.5rem) var(--space-4, 1rem);
      border-radius: var(--border-radius, 0.75rem);
      color: var(--color-text-secondary, #a0a0b8);
      font-size: var(--text-sm, 0.875rem);
      font-weight: var(--font-medium, 500);
      text-decoration: none;
      transition: all var(--transition-fast, 0.15s ease);
      cursor: pointer;
    }
    .nav-link { color: var(--color-text-primary); }
    .nav-link:hover {
      color: var(--color-accent);
      background: var(--color-surface);
    }
    .nav-link--active {
      color: var(--color-accent, #00d4ff);
      background: var(--color-accent-dim, rgba(0, 212, 255, 0.15));
    }
    .nav-link--signin {
      background: var(--color-surface);
      color: var(--color-accent);
      margin-left: var(--space-2, 0.5rem);
      border: 1px solid var(--color-border);
      box-shadow: 0 8px 22px color-mix(in srgb, var(--color-text-primary) 12%, transparent);
    }
    .nav-link--signin:hover {
      background: white;
      color: var(--color-accent-hover);
      transform: translateY(-1px);
    }
    .hamburger {
      display: none;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 5px;
      width: 2.75rem;
      height: 2.75rem;
      padding: 0;
      background: none;
      border: none;
      cursor: pointer;
    }
    .hamburger span {
      display: block;
      width: 24px;
      height: 2px;
      background: var(--color-text-primary, #f0f0f5);
      border-radius: 2px;
      transition: all var(--transition-fast, 0.15s ease);
    }
    .hamburger span { background: var(--color-text-primary); }
    .hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
    .hamburger.open span:nth-child(2) { opacity: 0; }
    .hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

    @media (max-width: 1024px) {
      .hamburger { display: flex; }
      nav {
        display: none;
        position: absolute;
        top: var(--header-height, 4rem);
        left: 0;
        right: 0;
        background: var(--color-bg-glass);
        backdrop-filter: blur(20px);
        flex-direction: column;
        padding: var(--space-4, 1rem);
        border-bottom: 1px solid var(--color-border);
        gap: var(--space-1, 0.25rem);
      }
      nav.open {
        display: flex;
      }
      .nav-link {
        display: flex;
        align-items: center;
        min-height: 2.75rem;
        box-sizing: border-box;
        width: 100%;
        padding: var(--space-3, 0.75rem) var(--space-4, 1rem);
      }
      .nav-link--signin {
        margin-left: 0;
        text-align: center;
      }
      .theme-toggle {
        align-self: center;
      }
      .header-actions { margin-left: 0; }
    }
  `;

  constructor() {
    super();
    this._menuOpen = false;
    this._scrolled = false;
    this._pages = [];
    this._currentSlug = router.currentSlug || 'home';
    this._settings = null;

    this._onScroll = () => {
      this._scrolled = window.scrollY > 50;
      this.classList.toggle('scrolled', this._scrolled);
      this.requestUpdate();
    };
    this._onStoreUpdate = () => {
      this._pages = store.getNavPages();
      this._settings = store.getSiteSettings();
      this.requestUpdate();
    };
    this._onRouteChange = (e) => {
      this._currentSlug = e.detail.slug || 'home';
      this.classList.add('light');
      this.requestUpdate();
    };
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('scroll', this._onScroll, { passive: true });
    router.addEventListener('route-changed', this._onRouteChange);
    this._unsub = store.subscribe(this._onStoreUpdate);
    this._pages = store.getNavPages();
    this._settings = store.getSiteSettings();
    this.classList.add('light');
    this.classList.toggle('scrolled', window.scrollY > 50);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this._onScroll);
    router.removeEventListener('route-changed', this._onRouteChange);
    if (this._unsub) this._unsub();
  }

  _navigate(slug) {
    router.navigate(slug);
    this._menuOpen = false;
    this.requestUpdate();
  }

  _href(slug) {
    const path = router.pathFor(slug);
    return withBuildPrefix(router.isDraft ? '/drafts' + path : path);
  }

  _toggleMenu() {
    this._menuOpen = !this._menuOpen;
    this.requestUpdate();
  }

  _toggleTheme() {
    const root = document.documentElement;
    const next = (root.dataset.theme === 'dark') ? 'light' : 'dark';
    root.dataset.theme = next;
    localStorage.setItem('site-theme', next);
    this.requestUpdate();
  }

  _renderThemeToggle() {
    if (!__SITE_CONFIG__.features?.theme_toggle) return '';
    const isDark = document.documentElement.dataset.theme === 'dark';
    return html`
      <button class="theme-toggle" @click=${this._toggleTheme}
              aria-label=${isDark ? 'Switch to day mode' : 'Switch to night mode'}
              title=${isDark ? 'Day mode' : 'Night mode'}>
        ${isDark
          ? html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`
          : html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`}
      </button>
    `;
  }

  _renderSearchButton() {
    const search = __SITE_CONFIG__.features?.search;
    if (!search) return '';
    const route = typeof search === 'object' ? (search.route || 'search') : 'search';
    const label = typeof search === 'object' ? (search.label || 'Search') : 'Search';
    return html`<site-search-button .href=${this._href(route)} .label=${label}
      .active=${this._isActive(route)}></site-search-button>`;
  }

  _isActive(slug) {
    const current = router.currentSlug || 'home';
    return slug === current;
  }

  render() {
    const navPages = this._pages.filter(p => p.slug !== 'signin' && p.slug !== 'signup');
    const brand = this._settings?.brand || {};
    const header = this._settings?.header || {};

    return html`
      <div class="header-inner">
        <a class="logo" href=${this._href('home')} @click=${(e) => { e.preventDefault(); this._navigate('home'); }}>
          ${brand.logo_image
            ? html`<img class="logo-image" src=${browserUrl(brand.logo_image)} alt=${brand.name || ''}>`
            : html`<div class="logo-icon">${brand.logo_text || ''}</div><span class="logo-text">${brand.name || ''}</span>`}
        </a>

        <button
          class="hamburger ${this._menuOpen ? 'open' : ''}"
          @click=${this._toggleMenu}
          aria-label=${header.menu_toggle_label || ''}
        >
          <span></span><span></span><span></span>
        </button>

        <nav class=${this._menuOpen ? 'open' : ''}>
          ${navPages.map(p => html`
            <a class="nav-link ${this._isActive(p.slug) ? 'nav-link--active' : ''}"
               href=${this._href(p.slug)}
               @click=${(e) => { e.preventDefault(); this._navigate(p.slug); }}>
              ${p.title}
            </a>
          `)}
          ${header.sign_in_label ? html`
            <a class="nav-link nav-link--signin"
               href=${router.pathFor(header.sign_in_slug || 'signin')}
               @click=${(e) => { e.preventDefault(); this._navigate(header.sign_in_slug || 'signin'); }}>
              ${header.sign_in_label}
            </a>
          ` : ''}
          ${__SITE_CONFIG__.features?.search || __SITE_CONFIG__.features?.theme_toggle ? html`
            <div class="header-actions">
              ${this._renderSearchButton()}
              ${this._renderThemeToggle()}
            </div>
          ` : ''}
        </nav>
      </div>
    `;
  }
}

customElements.define('site-header', SiteHeader);
